import { motion } from 'framer-motion';
import { useLanguage } from '../../i18n/LanguageContext';
import { appendTrackingParams } from '../../utils/utm';

export default function Pricing() {
  const { t } = useLanguage();
  return (
    <section id="pricing" className="bg-ev-cream py-24 md:py-32 px-6 md:px-10" data-testid="pricing-section">
      <div className="max-w-[1500px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-[720px] mx-auto text-center mb-16 md:mb-20">
          <h2
            className="font-display font-light text-ev-ink lowercase mb-4"
            style={{ fontSize: 'clamp(2rem, 5vw, 4rem)', letterSpacing: '-0.03em' }}>
            {t.pr_h}
          </h2>
          <p className="font-body text-sm md:text-base text-ev-gray">{t.pr_sub}</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-[1100px] mx-auto">
          {t.pr_plans.map((plan, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className={`rounded-[1.5rem] p-7 md:p-8 flex flex-col relative ${
                plan.featured ? 'bg-ev-ink text-white lg:-translate-y-3' : 'bg-white text-ev-ink'
              }`}
              data-testid={`pricing-card-${i}`}>
              {plan.featured && (
                <span className="absolute -top-3 left-7 bg-ev-lime text-ev-ink font-body text-[10px] uppercase tracking-[0.2em] font-semibold px-3 py-1 rounded-full">{t.pr_recommended}</span>
              )}
              <p className={`font-body text-xs uppercase tracking-[0.2em] mb-1 ${plan.featured ? 'text-white/60' : 'text-ev-gray'}`}>{plan.name}</p>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="font-display text-4xl md:text-5xl">{plan.price}</span>
                <span className={`font-body text-sm ${plan.featured ? 'text-white/60' : 'text-ev-gray'}`}>{plan.period}</span>
              </div>
              {plan.trial && (
                <p className={`font-body text-xs mb-2 ${plan.featured ? 'text-ev-lime' : 'text-ev-lime-dim'}`} data-testid={`pricing-trial-${i}`}>{plan.trial}</p>
              )}
              {plan.annual && (
                <p className={`font-body text-xs mb-3 ${plan.featured ? 'text-white/40' : 'text-ev-gray-light'}`}>{plan.annual}</p>
              )}
              <p className={`font-body italic text-sm mb-6 lowercase ${plan.featured ? 'text-ev-lime' : 'text-ev-gray'}`}>{plan.desc}</p>

              {plan.features_prefix && (
                <p className={`font-body text-[11px] uppercase tracking-wider mb-3 ${plan.featured ? 'text-ev-lime' : 'text-ev-lime-dim'}`}>{plan.features_prefix}</p>
              )}
              <ul className="space-y-2.5 mb-8 flex-1">
                {plan.features.map((f, j) => (
                  <li key={j} className="flex items-start gap-2.5">
                    <span className={`flex-shrink-0 w-1 h-1 rounded-full mt-2 ${plan.featured ? 'bg-white/50' : 'bg-ev-gray-light'}`} />
                    <span className={`font-body text-[13.5px] leading-relaxed ${plan.featured ? 'text-white/85' : 'text-ev-ink/75'}`}>{f}</span>
                  </li>
                ))}
              </ul>

              <a
                href={appendTrackingParams(t.pr_cta_url)}
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center justify-center px-6 py-3 font-body text-xs uppercase tracking-[0.15em] font-medium rounded-full transition-colors ${
                  plan.featured
                    ? 'bg-ev-lime hover:bg-ev-lime-dim text-ev-ink'
                    : 'bg-ev-ink hover:bg-ev-gray-dark text-white'
                }`}
                data-testid={`pricing-cta-${i}`}>
                {t.pr_cta}
              </a>
            </motion.div>
          ))}
        </div>
        {t.pr_annual_note && (
          <p className="font-body text-xs text-ev-gray-light text-center mt-8">{t.pr_annual_note}</p>
        )}
      </div>
    </section>
  );
}
